const hasNumericCoord = (value) => value != null && value !== ''

const parseLatLng = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  if (!hasNumericCoord(value.lat) || !hasNumericCoord(value.lng)) return null
  const lat = Number(value.lat)
  const lng = Number(value.lng)
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null
  return { lat, lng }
}

export const parseAroundLatLng = (value) => {
  if (typeof value !== 'string') return null
  const [latPart, lngPart, extra] = value.split(',').map((part) => part.trim())
  if (extra != null || !hasNumericCoord(latPart) || !hasNumericCoord(lngPart)) return null
  return parseLatLng({ lat: latPart, lng: lngPart })
}

const distanceSquared = (a, b) => {
  const dLat = a.lat - b.lat
  const dLng = a.lng - b.lng
  return (dLat * dLat) + (dLng * dLng)
}

const pointsFromGeoloc = (geoloc) => {
  const asObject = parseLatLng(geoloc)
  if (asObject) return [asObject]
  if (!Array.isArray(geoloc)) return []
  return geoloc.map(parseLatLng).filter(Boolean)
}

const closestPoint = (points, searchCenter) => {
  const center = parseLatLng(searchCenter)
  if (!center) return points[0]
  return points.reduce((closest, point) => (
    distanceSquared(center, point) < distanceSquared(center, closest) ? point : closest
  ))
}

/**
 * Resolve a single { lat, lng } from a coachProfiles Algolia hit.
 * `_geoloc` may be a single object (today) or an array of facility coords (after
 * pga-backend multi-facility indexing). InstantSearch aroundLatLng is unchanged.
 *
 * @param {object} [hit]
 * @param {{ searchCenter?: { lat: number, lng: number } }} [options]
 * @returns {{ lat: number, lng: number } | null}
 */
export const getCoachHitCoordinates = (hit, { searchCenter } = {}) => {
  const record = hit || {}
  const matched = parseLatLng(record._rankingInfo?.matchedGeoLocation)
  if (matched) return matched

  const points = pointsFromGeoloc(record._geoloc)
  if (!points.length) return null
  return closestPoint(points, searchCenter)
}

export const parseCoachLatLng = parseLatLng

/**
 * Pick the nested `facilities[]` entry a coachProfiles hit should display.
 * Multi-facility coaches are indexed with one `_geoloc` point per visible facility,
 * so the facility closest to Algolia's matched point (or the search center) wins.
 * Falls back to the primary facility (`facilities[0]`) without geo context.
 *
 * @param {object} [hit]
 * @param {{ searchCenter?: { lat: number, lng: number }, externalFacilityId?: string }} [options]
 * @returns {object|null}
 */
export const getCoachHitFacility = (hit, { searchCenter, externalFacilityId } = {}) => {
  const facilities = Array.isArray(hit?.facilities) ? hit.facilities.filter(Boolean) : []
  if (!facilities.length) return null

  if (externalFacilityId != null && externalFacilityId !== '') {
    const filtered = facilities.find(
      (facility) => String(facility.externalFacilityId) === String(externalFacilityId),
    )
    if (filtered) return filtered
  }

  const reference = parseLatLng(hit._rankingInfo?.matchedGeoLocation) || parseLatLng(searchCenter)
  if (!reference) return facilities[0]

  const located = facilities
    .map((facility) => ({ facility, point: parseLatLng(facility) }))
    .filter(({ point }) => point)
  if (!located.length) return facilities[0]

  return located.reduce((closest, candidate) => (
    distanceSquared(reference, candidate.point) < distanceSquared(reference, closest.point)
      ? candidate
      : closest
  )).facility
}

/**
 * "City, ST" for the facility chosen by `getCoachHitFacility`, falling back to
 * the profile's top-level (primary) city/state.
 *
 * @param {object} [hit]
 * @param {{ searchCenter?: { lat: number, lng: number }, externalFacilityId?: string }} [options]
 * @returns {string|null}
 */
export const getCoachHitPlace = (hit, options) => {
  const facility = getCoachHitFacility(hit, options)
  const city = (facility?.city || hit?.city || '').trim()
  const state = (facility?.state || hit?.state || '').trim()
  if (city && state) return `${city}, ${state}`
  return city || state || null
}

const METERS_PER_MILE = 1609.34

/**
 * Directory-card copy for Algolia `_rankingInfo.matchedGeoLocation.distance`
 * (meters). Returns null when InstantSearch did not run a geo query.
 *
 * @param {number|string|null|undefined} distanceMeters
 * @returns {string|null}
 */
export const formatCoachHitDistanceLabel = (distanceMeters) => {
  if (distanceMeters == null || distanceMeters === '') return null
  const meters = Number(distanceMeters)
  if (!Number.isFinite(meters) || meters < 0) return null
  const miles = meters / METERS_PER_MILE
  if (miles < 1) return 'Less than a mile away'
  const rounded = Math.round(miles)
  return rounded === 1 ? '1 mile away' : `${rounded} miles away`
}
